
import { useState, useEffect } from 'react';
import { usePriceMonitor } from '../../hooks/usePriceMonitor';

interface PriceMonitorSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const PriceMonitorSidebar = ({ isOpen, onClose }: PriceMonitorSidebarProps) => {
  const { trackedProducts, removeFromTracking, setTargetPrice, removeAlert } = usePriceMonitor();
  const [targetPriceInputs, setTargetPriceInputs] = useState<{ [key: number]: string }>({});
  const [activeTab, setActiveTab] = useState<'tracked' | 'alerts'>('tracked');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.getElementById('price-monitor-sidebar');
      const target = event.target as Node;
      if (isOpen && sidebar && !sidebar.contains(target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSetAlert = (productId: number) => {
    const targetPrice = parseFloat(targetPriceInputs[productId]);
    if (!isNaN(targetPrice) && targetPrice > 0) {
      setTargetPrice(productId, targetPrice);
      setTargetPriceInputs(prev => ({ ...prev, [productId]: '' }));
    }
  };

  const allAlerts = trackedProducts.flatMap(p => p.alerts);

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" />
      )}

      <div
        id="price-monitor-sidebar"
        className={`fixed top-0 right-0 h-full w-[420px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Price Monitor</h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>
            
            {/* Tabs */}
            <div className="flex bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setActiveTab('tracked')}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'tracked'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Tracked ({trackedProducts.length})
              </button>
              <button
                onClick={() => setActiveTab('alerts')}
                className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === 'alerts'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Alerts ({allAlerts.length})
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'tracked' ? (
              trackedProducts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-line-chart-line text-5xl text-gray-300"></i>
                  </div>
                  <p className="text-gray-500 text-lg">No products tracked</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Start tracking products to monitor price changes
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {trackedProducts.map((product) => (
                    <div
                      key={product.id}
                      className="bg-gray-50 rounded-lg p-4"
                    >
                      <div className="flex items-start space-x-4">
                        <div className="w-16 h-16 bg-white rounded-lg overflow-hidden flex-shrink-0">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-contain object-top"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {product.name}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1">{product.retailer}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <span className="text-lg font-bold text-gray-900">
                              ${product.currentPrice}
                            </span>
                            <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded">
                              Low: ${product.lowestPrice}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFromTracking(product.id)}
                          className="text-gray-400 hover:text-red-500 cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>

                      {/* Price History Mini Chart */}
                      <div className="mt-4 h-12 flex items-end gap-1">
                        {product.priceHistory.map((point, idx) => {
                          const maxPrice = Math.max(...product.priceHistory.map(p => p.price));
                          const minPrice = Math.min(...product.priceHistory.map(p => p.price));
                          const range = maxPrice - minPrice || 1;
                          const height = ((point.price - minPrice) / range) * 100;
                          return (
                            <div
                              key={idx}
                              className="flex-1 bg-teal-500 rounded-t opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
                              style={{ height: `${Math.max(height, 10)}%` }}
                              title={`${point.date}: $${point.price}`}
                            />
                          );
                        })}
                      </div>

                      {/* Set Price Alert */}
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-2">Set price alert</p>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                            <input
                              type="number"
                              placeholder="Target price"
                              value={targetPriceInputs[product.id] || ''}
                              onChange={(e) => setTargetPriceInputs(prev => ({
                                ...prev,
                                [product.id]: e.target.value
                              }))}
                              className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                          </div>
                          <button
                            onClick={() => handleSetAlert(product.id)}
                            className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Set Alert
                          </button>
                        </div>
                        
                        {/* Existing Alerts */}
                        {product.alerts.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {product.alerts.map(alert => (
                              <div
                                key={alert.id}
                                className="flex items-center justify-between bg-white px-3 py-2 rounded-lg"
                              >
                                <div className="flex items-center gap-2">
                                  <i className="ri-notification-3-line text-teal-600"></i>
                                  <span className="text-sm text-gray-700">
                                    Alert at ${alert.targetPrice}
                                  </span>
                                </div>
                                <button
                                  onClick={() => removeAlert(product.id, alert.id)}
                                  className="text-gray-400 hover:text-red-500 cursor-pointer"
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              allAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-notification-off-line text-5xl text-gray-300"></i>
                  </div>
                  <p className="text-gray-500 text-lg">No price alerts</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Set target prices to get notified when prices drop
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {allAlerts.map((alert) => (
                    <div
                      key={alert.id}
                      className="flex items-center gap-4 bg-gray-50 rounded-lg p-4"
                    >
                      <div className="w-12 h-12 bg-white rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={alert.productImage}
                          alt={alert.productName}
                          className="w-full h-full object-contain object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-gray-900 truncate">
                          {alert.productName}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-gray-600">
                            Current: ${alert.currentPrice}
                          </span>
                          <i className="ri-arrow-right-line text-gray-400"></i>
                          <span className="text-sm font-medium text-teal-600">
                            Target: ${alert.targetPrice}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeAlert(alert.productId, alert.id)}
                        className="text-gray-400 hover:text-red-500 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t bg-gray-50">
            <div className="flex items-center gap-3 text-sm text-gray-600">
              <i className="ri-information-line text-teal-600"></i>
              <span>Prices are updated every hour from multiple retailers</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PriceMonitorSidebar;
